import { useEffect, useState } from 'react';
import { useAuth } from '../../../../auth/AuthContext';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { Select } from '../../../../components/inputs/Select';
import { TextField } from '../../../../components/inputs/TextField';
import { InlineAddTable, InlineRow } from '../../../../components/inline-add-table/InlineAddTable';
import { TdSeamless } from '../../../../components/inline-add-table/cells';
import { HelpTip } from '../../../../components/HelpTip';
import { PreviewTable } from '../../../../components/PreviewTable';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { uid } from '../../../../lib/uid';
import { orgUnitsApi } from '../../organization/organizationApi';
import { positionsApi } from '../../positions/positionsApi';
import type { PositionOption } from '../../positions/positionTypes';
import type { OrgUnitOption } from '../../organization/organizationTypes';
import { plantillaApi, salaryGradesApi } from '../plantillaApi';
import { samplePlantillaPost } from '../utils/samplePlantillaPost';
import {
  SALARY_TYPE,
  salaryTypeLabel,
  type PlantillaPositionListItem,
  type SalaryGradeOption,
} from '../plantillaTypes';

/**
 * What the slots grid is, said once — in the HelpTip beside its label and in the DOM for a
 * reader who cannot reach a tooltip. Two copies of a sentence is how they come to disagree.
 */
const SLOTS_HELP =
  'One row per item — one person each. The plantilla assigns the item numbers on save and ' +
  'never reissues one, so a removed seat’s number is not handed to another.';

/**
 * Create or edit one authorized post.
 *
 * Two pickers and one read-only detail: the position comes from the catalog, the unit from
 * the chart, and the grade is DISPLAYED rather than chosen — a designation carries its grade,
 * so the server copies it from the chosen title and the form shows what that will be. The
 * amount beside it is read from the salary schedule, because a post is pegged to a grade
 * rather than to one tranche's rate.
 */
export function PlantillaPositionModal({
  open,
  editing,
  defaultUnitId,
  onClose,
  onSaved,
  onCreated,
}: {
  open: boolean;
  /** Null creates. */
  editing: PlantillaPositionListItem | null;
  /**
   * The unit the list is currently filtered to, if exactly one — the picker opens on it.
   *
   * A suggestion, not a lock: the reader narrowed the pattern to a unit and is now adding a
   * post, so that unit is the answer nine times in ten. They can still pick another, which is
   * why this is a seeded value rather than a disabled field.
   */
  defaultUnitId?: string | null;
  onClose: () => void;
  onSaved: () => void;
  /** The post just created, for the caller to open its profile. */
  onCreated?: (position: PlantillaPositionListItem) => void;
}) {
  /*
    Adding items to an existing plantilla is a headcount change, which carries
    `plantilla.manage-slots` rather than `plantilla.create` — two different privileges, and a
    caller can hold one without the other. Read here so the form states what will happen
    instead of promising an append the server will refuse.
  */
  const { hasPermission } = useAuth();
  const canAddItems = hasPermission('plantilla.manage-slots');

  // Seeded once, on mount. The parent mounts this only while it is open.
  const [positionId, setPositionId] = useState<string | null>(editing?.positionId ?? null);
  const [unitId, setUnitId] = useState<string | null>(editing?.orgUnitId ?? defaultUnitId ?? null);

  /*
    The post's own id, for the edit form to show. Never sent and never previewed on a create:
    the server assigns it inside the write.
  */
  const [code] = useState(editing?.code ?? '');

  /*
    THE ITEMS, ONE ROW EACH — product call, 09 Oct 2026, replacing a `Slots` number box.

    A headcount typed as a figure asks the reader to count what they are authorizing; the rows
    ARE the count, and each one shows the item number it will be given. Same grid every other
    add-many surface uses, so the plantilla stops being the one place a sub-record is a number.

    The row carries nothing but its key. **The reader does not number a seat** — an item number
    is `{Code}-{NNN}`, assigned by the server off a sequence that counts past every item the
    post has ever had — so the cell PREVIEWS what is coming rather than asking for it.
  */
  const [slotRows, setSlotRows] = useState<{ key: string }[]>(() => [{ key: uid() }]);

  /*
    The highest sequence the already-authorized post has ever issued, deleted items included —
    what the preview counts on from. Null while there is nothing to add to, or until the fetch
    lands, and the cells then say the number is assigned on save rather than guessing one.
  */
  const [highestSequence, setHighestSequence] = useState<number | null>(null);

  const [units, setUnits] = useState<OrgUnitOption[]>([]);
  const [grades, setGrades] = useState<SalaryGradeOption[]>([]);
  const [positions, setPositions] = useState<PositionOption[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(true);

  /*
    The plantilla that already holds this unit and designation, if there is one.

    A unit does not hold the same designation twice — the number of people it may employ on
    those terms is the ITEMS, not a second post beside the first — so when the pair is already
    authorized, this form adds to what is there rather than creating a rival record. Undefined
    means "not looked up yet"; null means "looked up, nothing there".
  */
  const [existing, setExisting] = useState<PlantillaPositionListItem | null | undefined>(undefined);

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    Promise.all([
      orgUnitsApi.options(undefined, controller.signal),
      salaryGradesApi.options(controller.signal),
      positionsApi.options(controller.signal),
    ])
      .then(([loadedUnits, loadedGrades, loadedPositions]) => {
        setUnits(loadedUnits);
        setGrades(loadedGrades);
        setPositions(loadedPositions);
      })
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') return;
        const failure = caught instanceof ApiError ? caught : new ApiError('Could not load the pickers.', 0);

      // No focus call: a failed picker fetch names no field, so there is nothing to point at.
      setError(failure);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingOptions(false);
      });

    return () => controller.abort();
  }, []);

  /*
    Looked up whenever the pair is complete, and re-looked-up when either half changes. Only
    on a create: an edit is already about one post, and its own identity is not in question.

    The answer is a fact about the database, so it is fetched rather than derived from the
    list page behind the modal — which may be filtered, paged, or minutes stale.
  */
  useEffect(() => {
    if (editing !== null || !unitId || !positionId) {
      setExisting(undefined);
      return;
    }

    const controller = new AbortController();

    plantillaApi
      .list({ unit: unitId, positionId, status: 'active', pageSize: 1 }, controller.signal)
      .then((page) => setExisting(page.items[0] ?? null))
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') return;
        /*
          Unknown rather than "nothing there": claiming the pair is free would promise a new
          post and then hit the server's own guard on save. The reader is told nothing and the
          save reports whatever actually happens.
        */
        setExisting(undefined);
      });

    return () => controller.abort();
  }, [editing, unitId, positionId]);

  /*
    What the post about to be added to has already issued. DELETED ITEMS INCLUDED — the server
    numbers past them so a removed seat's number is never handed to a different one, and a
    preview that counted only live items would promise numbers the save will not use.

    Only on the add-to-existing path. A brand-new post has no items and no code yet — the code
    is assigned inside the write — so there is nothing to count from and the cells say so.
  */
  useEffect(() => {
    if (editing !== null || !existing) {
      setHighestSequence(null);
      return;
    }

    const controller = new AbortController();

    plantillaApi
      .slots(existing.id, true, controller.signal)
      .then((items) =>
        setHighestSequence(items.reduce((highest, i) => Math.max(highest, i.sequenceNumber), 0)),
      )
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') return;

        // Unknown rather than a guess: a preview that cannot be derived is not drawn.
        setHighestSequence(null);
      });

    return () => controller.abort();
  }, [editing, existing]);

  /*
    THE ROWS ARE THE COUNT. Nothing parses a figure any more, so the only way the number can be
    invalid is the authorized ceiling the server itself holds at 200.
  */
  const slots = slotRows.length;
  const slotsValid = slots <= 200;

  /**
   * The item number a row will be given, or null where it cannot be known.
   *
   * Derived, never stored and never sent: the server assigns the number inside the write. It is
   * shown because a reader authorizing seats on an existing post is usually reading the numbers
   * off a paper plantilla, and the row saying which one it becomes is the whole value of the
   * grid over a count.
   */
  function previewItemNumber(index: number) {
    if (!existing || highestSequence === null) return null;

    return `${existing.code}-${String(highestSequence + index + 1).padStart(3, '0')}`;
  }

  function addSlotRow() {
    setSlotRows((current) => [...current, { key: uid() }]);
  }

  // Neither the id nor the order is a field any more, so neither is a condition.
  const valid = Boolean(positionId) && Boolean(unitId) && (editing !== null || slotsValid);
  const canSave = valid && confirmed && !saving;

  /*
    The grade this post carries, and it is NOT a field.

    A designation carries its grade — the catalog is where that is decided — so the form shows
    what the chosen title brings rather than asking again. It reads from the CHOSEN TITLE on
    both paths, which is also where the list reads it: attaching or changing a grade in the
    catalog reaches every post drawn from it at once, so a post and its designation can never
    disagree about what it is authorized at.
  */
  const chosen = positionId === null ? null : positions.find((p) => p.id === positionId);

  const grade = {
    salaryGrade: chosen?.salaryGrade ?? null,
    salaryGradeType: chosen?.salaryGradeType ?? null,
    salaryStep: chosen?.salaryStep ?? null,
  };

  /*
    What that grade and step currently pay, from the schedule the form already loaded. Read
    here rather than stored on the post: a post is pegged to a grade, and a stored amount
    would go stale the moment a new tranche is published.
  */
  const rate =
    grade.salaryGrade == null
      ? null
      : (grades.find(
          (g) =>
            g.grade === grade.salaryGrade &&
            g.step === (grade.salaryStep ?? 1) &&
            g.type === (grade.salaryGradeType ?? 0),
        )?.currentMonthlyAmount ?? null);

  /**
   * Fills every required field so the primary action enables on the first click.
   *
   * The two foreign keys are picked from the options already loaded rather than invented —
   * an id the generator made up fails the server's "it must exist and be active" check, which
   * reads as a bug in the form. The grade needs nothing here: it follows whichever title is
   * picked.
   */
  function autogenerate() {
    // The id is not generated here: it is the server's, and the preview follows the title.
    const sample = samplePlantillaPost();

    // One row per item the sample authorizes — the grid is the field now.
    setSlotRows(Array.from({ length: Number(sample.initialSlots) }, () => ({ key: uid() })));

    if (positions.length > 0) {
      setPositionId(positions[Math.floor(Math.random() * positions.length)]!.id);
    }

    if (units.length > 0) {
      setUnitId(units[Math.floor(Math.random() * units.length)].id);
    }
  }

  async function save() {
    if (!canSave || !unitId || !positionId) return;

    setSaving(true);
    setError(null);

    try {
      if (editing) {
        await plantillaApi.update(editing.id, { positionId, orgUnitId: unitId });
      } else if (existing) {
        /*
          The pair is already authorized, so this is a headcount change to that plantilla —
          the items are added to its inventory. Creating a second post would split one
          headcount across two records that nothing keeps in step, and every count on the
          list would be right about each half and wrong about the unit.

          Zero is a legal slots value and a no-op here: there is nothing to add and nothing to
          create, so the form simply closes.
        */
        if (slots > 0) {
          await plantillaApi.addSlots(existing.id, slots);
        }

        onCreated?.(existing);
      } else {
        const created = await plantillaApi.create({
          positionId,
          orgUnitId: unitId,
          initialSlots: slots,
        });

        /*
          The post is the parent of its items, and the items are the point — so a create hands
          the caller the new row to open rather than dropping the reader back on the list to
          find what they just made.
        */
        onCreated?.(created);
      }

      onSaved();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save the position.', 0);

      setError(failure);

      // Say which field, show it, and — on a stepped form — go to its step.
      focusValidationError(failure, {
        idPrefix: 'plantilla-position',
        /*
          The fields whose input is not named after them. Without these the helper resolves
          nothing and the reader is told which field but never shown it — the case the
          development warning in `focusValidationError` exists to catch.
        */
        fieldIds: {
          positionId: 'plantilla-position-position',
          orgUnitId: 'plantilla-position-unit',
        },
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Edit plantilla' : 'New plantilla'}
      /*
        Two-column rows and a unit picker whose labels carry the unit name and its tier, so
        `md` clipped both. Matches OrgUnitModal, the other create form in this suite.
      */
      size="2xl"
      id="plantilla-position-modal"
      // Create only. On a form over existing data it would overwrite the record's real values.
      autogenerate={editing === null ? { onGenerate: autogenerate, disabled: saving } : undefined}
      confirm={{
        id: 'plantilla-position-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="plantilla-position-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="plantilla-position-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {/*
          The unit first, and full width: it is the question the rest of the form depends on.
          Which designations are worth authorizing, and whether one is already authorized
          here, are both answered per unit — so asking for it second would mean answering
          those twice.
        */}
        <Select
          id="plantilla-position-unit"
          label="Unit"
          required
          remote
          searchable
          loading={loadingOptions}
          description="The unit this post sits in. Managed under Organization."
          value={unitId}
          onChange={(next) => {
            setUnitId(next);

            /*
              Changing the unit clears the designation. The pair is what identifies an
              existing plantilla, so a title left over from the previous unit would leave the
              inventory below describing a post in a unit the reader has moved away from.
            */
            setPositionId(null);
          }}
          options={units.map((u) => ({ value: u.id, label: u.name, right: u.orgUnitTypeName }))}
          error={unitId ? undefined : 'Choose the unit this position sits in.'}
          disabled={saving}
        />

        {/*
          TWO COLUMNS ONLY WHERE THERE ARE TWO FIELDS. The post's id sits beside the
          designation on an edit; on a create the slots are their own grid below, so a
          half-width position picker would leave the row half empty under a full-width unit.
        */}
        <div className={editing ? 'grid grid-cols-1 gap-4 sm:grid-cols-2' : ''}>
          {/*
            Disabled until a unit is chosen, and it says why — a disabled control with no
            reason is a dead end. This is disabled for STATE, not for permission: the answer
            arrives as soon as the unit does.
          */}
          <Select
            id="plantilla-position-position"
            label="Position"
            required
            remote
            searchable
            loading={loadingOptions}
            description={
              unitId
                ? 'The catalog title this post is drawn from. Managed under Organization → Settings → Positions; its qualification standards apply to this post.'
                : 'Choose a unit first — a post is authorized in a unit, so the unit decides what is being added and where.'
            }
            value={positionId}
            onChange={setPositionId}
            options={positions.map((p) => ({ value: p.id, label: p.title, right: p.code }))}
            error={!unitId || positionId ? undefined : 'Choose the position this post is drawn from.'}
            disabled={saving || !unitId}
          />

          {/*
            On an EDIT the post's own id sits beside the designation — it is the same fact
            continued, and the items are added from the profile rather than here. On a create
            the slots are a GRID below instead, so the position takes the row alone.
          */}
          {editing ? (
            <TextField
              id="plantilla-position-code"
              label="Plantilla ID"
              description="Permanent — it numbers this post's items, and those numbers are never reissued."
              value={code}
              onChange={() => undefined}
              readOnly
              disabled
            />
          ) : null}
        </div>

        {/*
          THE ITEMS, as rows rather than as a figure.

          One row is one item. The reader adds a row per seat they are authorizing and reads
          the number it will carry beside it, which is what a count could never show — and the
          same add-many grid every other sub-record on the product uses.
        */}
        {editing === null ? (
          <div>
            {/*
              The label pattern the two read-only tables on this form already use, and the
              description in the HelpTip beside it rather than stacked under it — the product's
              own departure from reference §5, kept here so a form of fields and tables carries
              one treatment. The sentence is in the DOM `sr-only` as well, which is the half of
              that departure the shared inputs never skip.
            */}
            <div className="flex min-h-5 items-center gap-1.5 pb-1.5">
              <p className="text-sm text-slate-600">Slots</p>
              <HelpTip label={SLOTS_HELP} />
            </div>

            <p className="sr-only">{SLOTS_HELP}</p>

            <InlineAddTable
              headers={['Item number']}
              colWidths={[320]}
              flexColumn={0}
              rowCount={slotRows.length}
              onAddRow={addSlotRow}
              /*
                The ceiling the server holds, stated where the reader meets it. Nothing else can
                make a row incomplete: a row has no cell to fill in.
              */
              addDisabled={saving || !slotsValid}
              addDisabledReason="A post may authorize at most 200 items."
              addButtonId="plantilla-position-add-slot"
            >
              {slotRows.map((row, index) => {
                const preview = previewItemNumber(index);

                return (
                  <InlineRow
                    key={row.key}
                    idx={index + 1}
                    onRemove={() =>
                      setSlotRows((current) => {
                        const next = current.filter((r) => r.key !== row.key);

                        // Never nothing: an empty grid has no row to press a `+` from, so
                        // removing the last one would leave the form with no way back.
                        return next.length > 0 ? next : [{ key: uid() }];
                      })
                    }
                  >
                    {/*
                      READ-ONLY, and it states only what it can prove. On a post that already
                      exists the next numbers are known — the sequence counts on from the
                      highest it has ever issued — and on a new one neither the code nor the
                      sequence exists until the server writes it, so the cell says that
                      instead of printing a number the save would not use.
                    */}
                    <TdSeamless>
                      {preview === null ? (
                        <span className="truncate text-sm text-slate-400">Assigned on save</span>
                      ) : (
                        <span className="truncate font-mono text-sm text-slate-600" title={preview}>
                          {preview}
                        </span>
                      )}
                    </TdSeamless>
                  </InlineRow>
                );
              })}
            </InlineAddTable>
          </div>
        ) : null}


        {/*
          The grade, shown rather than asked for.

          A label/value table because none of it is a decision made here: the designation
          carries the grade and the step, and the schedule decides what they pay. A picker
          would be asking a question the catalog has already answered, and a second copy of
          the answer to keep in step with it.
        */}
        <div>
          <p className="pb-1.5 text-sm text-slate-600">Salary grade</p>

          <PreviewTable
            /*
              The Table Display View — the tinted, uppercase label column.

              Product call for this form: both tables here are read-only FACTS rather
              than fields, and in a stack of them the tint is what separates a key from
              a value at a glance. Recorded in CLAUDE.md, which previously scoped this
              variant to profile cards.
            */
            variant="table-display"
            rows={[
              {
                label: 'SG',
                value:
                  grade.salaryGrade == null ? null : (
                    <span className="font-mono tabular-nums">
                      {grade.salaryGrade}
                      {grade.salaryGradeType === SALARY_TYPE.health
                        ? ` (${salaryTypeLabel(grade.salaryGradeType)})`
                        : ''}
                    </span>
                  ),
              },
              {
                label: 'Step',
                value:
                  grade.salaryStep == null ? null : (
                    <span className="font-mono tabular-nums">{grade.salaryStep}</span>
                  ),
              },
              {
                label: 'Amount',
                value:
                  rate == null ? null : (
                    <span className="font-mono tabular-nums">
                      {rate.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  ),
              },
            ]}
          />
        </div>

        {/*
          What this unit already has for this designation, and what the save will do to it.

          Shown rather than refused: the reader is not making a mistake by naming a pair that
          exists — they are adding headcount to it, which is the only thing a second post
          could have meant. The numbers are the same three the list calls Inventory, so the
          form and the table describe one thing in one vocabulary.
        */}
        {editing === null && existing ? (
          <>
            {/*
              The banner carries the MESSAGE and nothing else — what is about to happen, in a
              sentence. The numbers are a separate thing to read, so they sit below it as
              their own table rather than inside the tint, where the banner's ground competed
              with the table's own label column for the same job.
            */}
            <div className="rounded-lg border border-blue-200 bg-blue-50/60 p-3">
              <p className="text-xs text-slate-700">
                <span className="font-medium">{existing.title}</span> is already authorized in
                this unit as <span className="font-mono">{existing.code}</span>.{' '}
                {!canAddItems
                  ? 'Adding items to it needs the manage-slots permission, which this account does not hold — the save will be refused.'
                  : slotsValid && slots > 0
                    ? `The ${slots} item${slots === 1 ? '' : 's'} you add will join its inventory — no second post is created.`
                    : 'Any items you add will join its inventory — no second post is created.'}
              </p>
            </div>

            <div>
              {/*
                "Inventory" — the same word the list's column group uses for these three
                numbers. One name for one set of facts, so a reader moving between the form
                and the table is not asked to work out that they are the same thing.
              */}
              <p className="pb-1.5 text-sm text-slate-600">Inventory</p>

              <PreviewTable
                /*
                  The Table Display View — the tinted, uppercase label column.

                  Product call for this form: both tables here are read-only FACTS rather
                  than fields, and in a stack of them the tint is what separates a key from
                  a value at a glance. Recorded in CLAUDE.md, which previously scoped this
                  variant to profile cards.
                */
                variant="table-display"
                rows={[
                  {
                    label: 'Items',
                    value: <span className="font-mono tabular-nums">{existing.authorizedSlots}</span>,
                  },
                  {
                    label: 'Vacant',
                    value: <span className="font-mono tabular-nums">{existing.vacantSlots}</span>,
                  },
                  {
                    label: 'Fulfilled',
                    value: <span className="font-mono tabular-nums">{existing.filledSlots}</span>,
                  },
                  {
                    label: 'After saving',
                    value:
                      slotsValid && slots > 0 ? (
                        <span className="font-mono tabular-nums">
                          {existing.authorizedSlots + slots}
                        </span>
                      ) : null,
                  },
                ]}
              />
            </div>
          </>
        ) : null}
      </div>
    </Modal>
  );
}
